import {Button, Popconfirm, TableProps, Tooltip} from "antd";
import React, {useState} from "react";
import {BasicTable} from "../../Common/BasicComponents/BasicTable";
import {AdminHttpService} from "../../../services/Http/SearchTableService";
import {FilterSelect} from "../../Common/BasicComponents/filters/FilterSelect";
import {
    InsuranceProviderAuthorization,
    InsuranceProviderAuthorizationOperationType,
    InsuranceProviderAuthorizationOperationTypeDescriptions
} from "../../../models/InsuranceProviderAuthorization";
import {PlusCircleOutlined, QuestionCircleOutlined} from "@ant-design/icons";
import {AuthService} from "../../../services/AuthService";
import {useModal} from "../../../utils/modals";
import {BasicFormMethod, BasicFormModal} from "../../Common/BasicComponents/BasicFormModal";
import {PageTitle} from "../../Common/PageTitle/PageTitle";
import {Utils} from "../../../services/Utils";
import {NotificationService} from "../../../services/NotificationService";

export interface InsuranceProviderAuthorizationsManagementPageProps {}

export type AnyObject = Record<string, any>;

const operationTypeOptions = Utils.enumToSelectOptions(InsuranceProviderAuthorizationOperationType);

function OperationTypeCell({value}: { value: string }) {
    const description = InsuranceProviderAuthorizationOperationTypeDescriptions[value as InsuranceProviderAuthorizationOperationType];
    if (!description) return <>{value}</>;
    return (
        <Tooltip title={description}>
            <span style={{cursor: 'help', borderBottom: '1px dotted #999'}}>{value}</span>
        </Tooltip>
    );
}

export function InsuranceProviderAuthorizationsManagementPage(props: InsuranceProviderAuthorizationsManagementPageProps) {
    const [refreshKey, setRefreshKey] = useState(0);

    const {
        isOpen: isCreateFormModalOpen,
        open: openCreateFormModal,
        close: closeCreateFormModal
    } = useModal();

    const handleDelete = async (id: number) => {
        try {
            await AdminHttpService.delete(AdminHttpService.URL_ADMIN_INSURANCE_PROVIDER_AUTHORIZATION, id);
            NotificationService.getInstance().openNotificationSuccess("Impostazione eliminata con successo");
            setRefreshKey(prev => prev + 1);
        } catch (e) {
            console.error(e);
            NotificationService.getInstance().openNotificationError("Impossibile eliminare l'impostazione");
        }
    };

    const handleCreateSuccess = () => {
        closeCreateFormModal();
        setRefreshKey(prev => prev + 1);
    };

    const columns: TableProps<InsuranceProviderAuthorization>['columns'] = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id'
        },
        {
            title: (
                <Tooltip title="Il codice univoco dell'assicurazione nel sistema di integrazione (es. FASCHIM, AXA, POSTEWELFARE).">
                    Codice Integrazione <QuestionCircleOutlined style={{color: '#1890ff'}}/>
                </Tooltip>
            ),
            dataIndex: 'integrationCode',
            key: 'integrationCode'
        },
        {
            title: (
                <Tooltip title="Il comportamento speciale abilitato per questa assicurazione nel flusso della pratica.">
                    Tipo Operazione <QuestionCircleOutlined style={{color: '#1890ff'}}/>
                </Tooltip>
            ),
            dataIndex: 'operationType',
            key: 'operationType',
            render: (value: string) => <OperationTypeCell value={value}/>
        },
        {
            title: 'Azioni',
            key: 'azioni',
            render: (_: any, record: InsuranceProviderAuthorization) => (
                <Popconfirm
                    title="Eliminare questa impostazione?"
                    description="L'operazione non può essere annullata."
                    onConfirm={() => handleDelete(record.id)}
                    okText="Sì, elimina"
                    cancelText="Annulla"
                >
                    <Button danger size="small">Elimina</Button>
                </Popconfirm>
            )
        }
    ];

    const searchFilters = [
        <FilterSelect
            dataKey={"insuranceProviderId"}
            placeholder={"Codice Integrazione"}
            showSearch={true}
            api={{
                url: AdminHttpService.URL_ADMIN_INSURANCE_PROVIDERS,
                value: "id",
                label: "integrationCode",
                sendAsObject: false,
                responseKey: "data"
            }}
        />,
        <FilterSelect
            dataKey={"operationType"}
            placeholder={"Tipo Operazione"}
            options={operationTypeOptions}
        />
    ];

    const insuranceProviderLabel = (
        <>
            Codice Integrazione{" "}
            <Tooltip title="Il codice univoco dell'assicurazione nel sistema di integrazione (es. FASCHIM, AXA, POSTEWELFARE).">
                <QuestionCircleOutlined style={{color: '#1890ff'}}/>
            </Tooltip>
        </>
    );

    const operationTypeLabel = (
        <>
            Tipo Operazione{" "}
            <Tooltip title="Il comportamento speciale abilitato per questa assicurazione nel flusso della pratica. Passaci sopra con il mouse per vedere la descrizione di ogni tipo.">
                <QuestionCircleOutlined style={{color: '#1890ff'}}/>
            </Tooltip>
        </>
    );

    const formInputFields = [
        <FilterSelect
            dataKey={"insuranceProviderId"}
            label={insuranceProviderLabel as any}
            placeholder={"Es. FASCHIM"}
            required={true}
            showSearch={true}
            api={{
                url: AdminHttpService.URL_ADMIN_INSURANCE_PROVIDERS,
                value: "id",
                label: "integrationCode",
                sendAsObject: false,
                responseKey: "data",
                searchEnabled: true
            }}
        />,
        <FilterSelect
            dataKey={"operationType"}
            label={operationTypeLabel as any}
            placeholder={"Seleziona tipo operazione"}
            required={true}
            options={operationTypeOptions}
        />
    ];

    return <>
        <PageTitle title={"Autorizzazioni Assicurazioni"}/>

        <BasicTable<InsuranceProviderAuthorization>
            key={refreshKey}
            columns={columns}
            urlPath={AdminHttpService.URL_ADMIN_INSURANCE_PROVIDER_AUTHORIZATION}
            filters={searchFilters}
        />

        <Button
            icon={<PlusCircleOutlined/>}
            type={"primary"}
            style={{float: 'right', marginTop: '10px', marginLeft: '10px'}}
            onClick={() => {
                AuthService.isAdmin() && openCreateFormModal();
            }}
        >
            Aggiungi impostazione
        </Button>

        {isCreateFormModalOpen &&
            <BasicFormModal<InsuranceProviderAuthorization>
                urlPath={AdminHttpService.URL_ADMIN_INSURANCE_PROVIDER_AUTHORIZATION}
                method={BasicFormMethod.CREATE}
                isOpen={isCreateFormModalOpen}
                onOk={handleCreateSuccess}
                onCancel={closeCreateFormModal}
                inputFields={formInputFields}
                title={"Aggiungi impostazione assicurazione"}
            />
        }
    </>;
}
